import { EventSourceParserStream } from "eventsource-parser/stream";

type StartChunk = { type: "start"; messageId?: string };
type TextStartChunk = { type: "text-start" };
type TextDeltaChunk = { type: "text-delta"; delta: string };
type TextEndChunk = { type: "text-end" };
type BuiltInChunk = StartChunk | TextStartChunk | TextDeltaChunk | TextEndChunk;

type ChunkListener<T> = (chunk: T) => void;

/**
 * A `ReadableStream` as something `for await` can walk.
 *
 * Note what is being iterated: this generator, never the stream. Streams are
 * async-iterable themselves in Chrome and Firefox, and in Safari from 26.4, and
 * `for await (const event of eventStream)` is what broke every iPhone older
 * than that. WebKit had no `[Symbol.asyncIterator]` to call, so the loop threw
 * before the first chunk and the search reported an error instead of an answer.
 * Async generators have been everywhere since 2018 and `getReader()` is as old
 * as streams, so between them the question does not arise.
 *
 * Leaving the loop early runs the `finally`, which is the other half of what
 * the built-in iterator did: cancelling closes the response instead of leaving
 * the socket waiting on a body nobody is going to read.
 */
async function* read<T>(stream: ReadableStream<T>): AsyncGenerator<T> {
  const reader = stream.getReader();

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) return;
      yield value;
    }
  } finally {
    // A no-op on a stream that closed by itself, and a rejection on one that
    // errored, which is how an abort arrives. Nothing here is waiting on it:
    // whatever errored the stream is already on its way up.
    void reader.cancel().catch(() => {});
  }
}

export class UIMessageStream<
  TChunk extends { type: string } = { type: string; [key: string]: unknown },
> {
  private body: ReadableStream<Uint8Array>;
  private listeners = new Map<string, Set<ChunkListener<any>>>();
  private _activeText = "";
  private _messageId = "";

  get activeText(): string {
    return this._activeText;
  }

  get messageId(): string {
    return this._messageId;
  }

  constructor(body: ReadableStream<Uint8Array>) {
    this.body = body;
  }

  on<K extends (TChunk | BuiltInChunk)["type"]>(
    type: K,
    listener: ChunkListener<Extract<TChunk | BuiltInChunk, { type: K }>>,
  ): () => void {
    if (!this.listeners.has(type)) {
      this.listeners.set(type, new Set());
    }
    this.listeners.get(type)!.add(listener);
    return () => this.listeners.get(type)?.delete(listener);
  }

  /** Reads the body to completion, emitting as it goes. */
  async start(): Promise<void> {
    const eventStream = this.body
      .pipeThrough(new TextDecoderStream() as any)
      .pipeThrough(new EventSourceParserStream());

    for await (const event of read(eventStream)) {
      if (event.data === "[DONE]") return;

      try {
        const chunk = JSON.parse(event.data) as TChunk | BuiltInChunk;
        this.handleChunk(chunk);
      } catch {
        // Skip malformed JSON
      }
    }
  }

  private handleChunk(chunk: TChunk | BuiltInChunk): void {
    switch (chunk.type) {
      case "start":
        this._messageId = (chunk as StartChunk).messageId ?? "";
        break;
      case "text-start":
        this._activeText = "";
        break;
      case "text-delta":
        this._activeText += (chunk as TextDeltaChunk).delta ?? "";
        break;
      case "text-end":
        break;
    }

    this.emit(chunk);
  }

  private emit(chunk: TChunk | BuiltInChunk): void {
    const listeners = this.listeners.get(chunk.type);
    if (listeners) {
      for (const listener of listeners) {
        listener(chunk);
      }
    }
  }
}
